"use client";

import Image from "next/image";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import AddToCartButton from "./add-to-cart-button";
import StarRating from "./star-rating";

interface ProductCardProps {
  title: string;
  price: string | number;
  mrp: string | number;
  image: string;
  rating?: number;
  handle: string;
  onAddToCart?: () => void;
  inStock?: boolean; // Whether the product is in stock
  isLoading?: boolean; // Whether inventory status is loading
  href?: string; // Optional href for the link
}

export default function ProductCard({
  title,
  price,
  mrp,
  image,
  rating,
  handle,
  onAddToCart,
  inStock = true, // Default to true
  isLoading = false,
  href,
}: ProductCardProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col group h-full justify-between px-1 w-full">
      <Link
        aria-label="product image"
        href={href ? href : `/products/${encodeURIComponent(handle)}`}
      >
        <div className="flex flex-col cursor-pointer">
          {/* Product Image */}
          <div className="relative w-full aspect-square mb-2 rounded-tl-[32px] rounded-br-[32px] overflow-hidden bg-gray-200">
            <Image src={image} alt={title} fill className="object-contain" />
          </div>

          {/* Product Title */}
          <h3 className="text-[16px] text-ellipsis font-['Ainslie_Sans_Condensed'] font-[700] text-[black] line-clamp-2 min-h-[40px] w-[130px] sm:w-[264px] lg:w-[250px] xl:w-[264px] ">
            {title}
          </h3>
        </div>
      </Link>

      <div className="w-full mt-1 pb-4 md:py-4">
        {/* Price and Rating Display */}
        <div className="flex flex-col mb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <span className=" text-[18px] md:text-[20px] font-['Ainslie_Sans_Condensed'] font-bold text-black">
                ₹ {Number(price) / 100}
              </span>
              {mrp && mrp !== "0" && mrp !== price && (
                <span className=" text-[14px] md:text-[16px] font-['Ainslie_Sans_Condensed'] text-gray-500">
                  MRP:{" "}
                  <span className="line-through">₹{Number(mrp) / 100}</span>
                </span>
              )}
            </div>
            <div className="mt-1 min-h-[16px] flex justify-start">
              {rating && <StarRating rating={rating} />}
            </div>
          </div>
        </div>

        {/* Add to Cart Button */}
        <div className="w-full -mt-1">
          <AddToCartButton
            onClick={onAddToCart}
            disabled={!inStock || isLoading}
            label={
              isLoading
                ? t("common.checking")
                : !inStock
                  ? t("common.outOfStock")
                  : undefined
            }
          />
        </div>
      </div>
    </div>
  );
}
